import { ConversationAdapter, Message } from '../contexts/ConversationContext';
import * as chatService from '../services/chatService';
import { Persona } from '../types/persona';

// Wave 6 Task 24: the persona-chat send-message API now returns the
// raw `process_with_persona` shape (response, metadata, tool_calls,
// turn_telemetry) — matching admin/work chat. The adapter wraps it
// into a Message so MessageBubble + Wave 7 consumers read a single
// shape: `message.metadata.tool_calls`, `message.metadata.turn_telemetry`.
export type ToolCallHandler = (toolCalls: any[]) => void;

interface PersonaSendMessageResponse {
  id: string;
  timestamp: string;
  response: string;
  metadata?: Record<string, any>;
  tool_calls?: any[];
  turn_telemetry?: Record<string, any> | null;
}

export class PersonaChatAdapter implements ConversationAdapter {
  private persona?: Persona;
  private onToolCalls?: ToolCallHandler;

  constructor(persona?: Persona, onToolCalls?: ToolCallHandler) {
    this.persona = persona;
    this.onToolCalls = onToolCalls;
  }

  async initialize(sessionId: string, metadata?: Record<string, any>): Promise<void> {
    if (metadata?.isNew && this.persona) {
      // Create a new chat session
      await chatService.createPersonaChat(
        this.persona.id,
        metadata?.initialMessage,
        metadata
      );
    }
  }

  async getMessages(sessionId: string): Promise<Message[]> {
    try {
      const messages = await chatService.getChatMessages(sessionId);
      return messages;
    } catch (error) {
      // Re-throw rather than return [] — an empty array is
      // indistinguishable from a legitimate empty-session response,
      // so swallowing turns a transient fetch failure into a silent
      // wipe of the user's chat history. Re-throwing routes the
      // caller's catch path which dispatches SET_ERROR without
      // clearing state.messages.
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  async sendMessage(sessionId: string, content: string, metadata?: Record<string, any>): Promise<Message> {
    const response = (await chatService.sendMessage(sessionId, content, metadata)) as unknown as PersonaSendMessageResponse;

    // Notify Wave 7 PersonaContextPanel of new tool calls — mirrors the
    // admin/work onToolCalls pattern.
    if (this.onToolCalls && response.tool_calls && response.tool_calls.length > 0) {
      this.onToolCalls(response.tool_calls);
    }

    return {
      id: response.id,
      content: response.response,
      role: 'assistant',
      timestamp: response.timestamp,
      metadata: {
        ...(response.metadata ?? {}),
        tool_calls: response.tool_calls ?? [],
        turn_telemetry: response.turn_telemetry ?? null,
      },
    };
  }
}
